import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Grid } from '@material-ui/core'
import { SupervisedUserCircleOutlined, FolderSpecialOutlined, RestoreOutlined, FolderOutlined } from '@material-ui/icons'
import { SiteStatusCard } from '../SiteStatusCard'
import * as navigator from '../../tools/Navigator'
import * as api from '../../tools/API'
import { FileAttachment, SiteInfoWebDetails } from '../../tools/API'
import { TeamCard, TeamCardType } from '../TeamCard'
import { LinkCard } from '../LinkCard'
import { FilesCard } from '../FilesCard'
import { CustomerInfoCard } from '../CustomerInfoCard'
import { ServiceDescriptionListCard } from '../ServiceDescriptionCard'
import { CalendarEventsCard } from '../CalendarEventsCard'
import { SiteEvaluationEventsCard } from '../SiteEvaluationEventsCard'
import { SiteInfoCard } from '../SiteInfoCard'
import { BackdropLoadingSpinner } from '../BackdropLoadingSpinner'

interface SiteDetailsLayoutProps {
  loadingSite: boolean
  children: ReactNode
}

export function SiteDetailsLayout({ loadingSite, children }: SiteDetailsLayoutProps) {
  const { t } = useTranslation()

  return (
    <Grid container spacing={3} direction="row">
      <BackdropLoadingSpinner open={loadingSite} message={t('siteLoadingMsg')} />
      {children}
    </Grid>
  )
}

interface SiteDetailsViewProps {
  siteInfoWebDetails?: SiteInfoWebDetails
  loadSite: () => Promise<void>
  siteId: number
}

export function SiteDetailsView({ siteInfoWebDetails, loadSite, siteId }: SiteDetailsViewProps) {
  const { t } = useTranslation()

  return (
    <>
      <Grid item xs={12} sm={12} md={6}>
        <Grid container spacing={2} direction="column" >
          <Grid item>
            <SiteInfoCard siteInfoWebDetails={siteInfoWebDetails} refreshSite={loadSite} />
          </Grid>
          <Grid item>
            <SiteStatusCard siteInfoWebDetails={siteInfoWebDetails} />
          </Grid>
          <Grid item>
            <SiteFilesCard siteId={siteId} files={siteInfoWebDetails?.files ?? []} />
          </Grid>
          <Grid item>
            <CalendarEventsCard siteId={siteId} />
          </Grid>
          <Grid item>
            <SiteEvaluationEventsCard siteId={siteId} />
          </Grid>
          <Grid item>
            <ServiceDescriptionListCard siteId={siteId} />
          </Grid>
          {/* To be implemented */}
          {/*<Grid item>
            <DetailsCard cardId='siteIns' title={t('siteInstructionsTitle')} subtitle={t('siteInstructionsSubtitle', {count: 2})} icon={<AssignmentOutlined/>}><div></div></DetailsCard>
          </Grid>*/}
          <Grid item>
            <TeamCard
              type={TeamCardType.ServiceTeam}
              ownerId={siteInfoWebDetails?.id}
              title={t('siteServiceTeamTitle')}
              icon={<SupervisedUserCircleOutlined />}
              grouped={true}
              onSitesClick={member => navigator.push(`/party/${member.id}/sites`, { title: t('logTableTitle', { name: member.firstname }) })}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <Grid container spacing={2} direction="column" >
          {siteInfoWebDetails?.customers.map(customer => (
            <Grid item key={customer.id}>
              <CustomerInfoCard customer={customer} showLink={true} />
            </Grid>
          ))}
          <Grid item>
            <TeamCard
              type={TeamCardType.SiteOthers}
              ownerId={siteInfoWebDetails?.id}
              title={t('siteOtherContactsTitle')}
              icon={<FolderSpecialOutlined />}
              grouped={false}
              onSitesClick={member => navigator.push(`/party/${member.id}/sites`, { title: t('logTableTitle', { name: member.firstname }) })}
            />
          </Grid>
          {/* Commented out temporarily until access control mechanisms etc are properly specified */}
          {/*<Grid item>
            <SiteFormCard siteId={site?.id}/>
          </Grid>*/}
          <Grid item>
            <LinkCard
              cardId='siteLog'
              title={t('logCardTitle')}
              icon={<RestoreOutlined />}
              onClick={() => navigator.push(`/site/${siteInfoWebDetails!.id}/log`, { title: t('logTableTitle', { name: siteInfoWebDetails!.name }) })}
            />
          </Grid>
          <Grid item>
            <LinkCard
              cardId='contractLines'
              title={t('siteContractLinesCardTitle')}
              icon={<FolderOutlined />}
              onClick={() => navigator.push(`/site/${siteInfoWebDetails!.id}/contract-lines`, siteInfoWebDetails)}
            />
          </Grid>
          {/* Commented out temporarily until new price list integration is implemented */}
          {/*<Grid item>
            <SitePriceListCard site={site}/>
          </Grid>*/}
        </Grid>
      </Grid>
    </>
  )
}

interface SiteFilesCardProp {
  siteId: number
  files: FileAttachment[]
}

function SiteFilesCard(p: SiteFilesCardProp) {
  const { t } = useTranslation()

  return (
    <FilesCard
      cardId='siteFiles'
      title={t('siteAttachmentsTitle')}
      grouped={true}
      files={p.files}
      fileSaver={(file, description, category) => api.addSiteInfoFile(p.siteId, file, description, category)}
      fileUpdater={(fileId, description, category) => api.updateSiteInfoFile(p.siteId, fileId, description, category)}
      fileReplacer={(fileId, file, description, category) => api.replaceSiteInfoFile(p.siteId, fileId, file, description, category)}
      targetId={p.siteId}
      newFileType='SITE'
      editableType='SITE'
    />
  )
}
